import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, User, Calendar, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArticleDetailPage() {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await axios.get(`${API}/articles/${articleId}`);
        setArticle(response.data);
      } catch (error) {
        console.error("Error fetching article:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [articleId]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-slate-200 rounded w-3/4" />
            <div className="h-64 bg-slate-100 rounded-2xl" />
            <div className="space-y-4">
              <div className="h-4 bg-slate-100 rounded w-full" />
              <div className="h-4 bg-slate-100 rounded w-full" />
              <div className="h-4 bg-slate-100 rounded w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen pt-20 bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Article not found</h1>
          <Link to="/insights">
            <Button>Back to Insights</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="article-detail-page">
      {/* Header */}
      <section className="py-8 bg-slate-50 border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/insights" className="inline-flex items-center gap-2 text-slate-600 hover:text-forest-700 mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Insights
          </Link>
        </div>
      </section>

      {/* Article Content */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Badge className="bg-forest-50 text-forest-700">{article.category}</Badge>
          <span className="flex items-center gap-1 text-sm text-slate-500">
            <Clock className="h-4 w-4" />
            {article.read_time}
          </span>
          <span className="flex items-center gap-1 text-sm text-slate-500">
            <Calendar className="h-4 w-4" />
            {new Date(article.date).toLocaleDateString('en-IN', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </span>
        </div>

        {/* Title */}
        <h1 className="font-montserrat text-3xl sm:text-4xl font-bold text-slate-900 mb-6" data-testid="article-title">
          {article.title}
        </h1>

        {/* Author */}
        <div className="flex items-center gap-3 mb-8 pb-8 border-b border-slate-100">
          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
            <User className="h-6 w-6 text-slate-500" />
          </div>
          <div>
            <p className="font-medium text-slate-900">{article.author}</p>
            <p className="text-sm text-slate-500">{article.author_title}</p>
          </div>
        </div>

        {/* Featured Image */}
        <img
          src={article.image}
          alt={article.title}
          className="w-full aspect-video object-cover rounded-2xl mb-8"
        />

        {/* Content */}
        <div className="prose prose-slate max-w-none">
          <p className="text-lg text-slate-600 leading-relaxed mb-6">
            {article.excerpt}
          </p>
          
          <h2 className="font-montserrat text-2xl font-semibold text-slate-900 mt-8 mb-4">Introduction</h2>
          <p className="text-slate-600 leading-relaxed">
            The global carbon market is undergoing rapid transformation, driven by increasingly stringent climate policies, 
            corporate net-zero commitments, and growing investor focus on ESG factors. This article explores the key trends 
            and implications for stakeholders across the carbon economy.
          </p>
          
          <h2 className="font-montserrat text-2xl font-semibold text-slate-900 mt-8 mb-4">Key Insights</h2>
          <p className="text-slate-600 leading-relaxed">
            Carbon pricing mechanisms are expanding globally, with more jurisdictions implementing emissions trading systems 
            or carbon taxes. The EU's CBAM represents a significant development that will impact global trade patterns, 
            particularly for exporters in emissions-intensive industries.
          </p>
          
          <h2 className="font-montserrat text-2xl font-semibold text-slate-900 mt-8 mb-4">Implications for India</h2>
          <p className="text-slate-600 leading-relaxed">
            India's carbon market is still evolving, with the Carbon Credit Trading Scheme providing a framework for 
            domestic emissions trading. Indian exporters, particularly in sectors like steel, cement, and aluminum, 
            must prepare for CBAM compliance to maintain market access to the EU.
          </p>
          
          <h2 className="font-montserrat text-2xl font-semibold text-slate-900 mt-8 mb-4">Conclusion</h2>
          <p className="text-slate-600 leading-relaxed">
            The transition to a low-carbon economy presents both challenges and opportunities. Organizations that 
            proactively engage with carbon markets and build sustainability capabilities will be better positioned 
            for long-term success in an increasingly climate-conscious world.
          </p>
        </div>

        {/* Share */}
        <div className="mt-12 pt-8 border-t border-slate-100">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">Share this article</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 bg-forest-50 rounded-2xl p-8 text-center">
          <h3 className="font-montserrat text-xl font-semibold text-slate-900 mb-2">
            Want to Learn More?
          </h3>
          <p className="text-slate-600 mb-4">
            Explore our insights on carbon markets and climate policy.
          </p>
          <Link to="/insights">
            <Button className="bg-forest-950 hover:bg-forest-900">
              Explore Insights
            </Button>
          </Link>
        </div>
      </article>
    </div>
  );
}
